import React, { useEffect, useState } from 'react'
import { Popup, Button, TextArea } from 'antd-mobile'
import '../styles/EditRecommendationModal.css'
import WeekDatePicker from '../Charts/WeekDatePicker'
import {
  categoryOptions,
  durationOptions,
  workoutTypeOptionsMap,
  intensityOptions,
  fieldDisplayNameMap
} from '../../../utils'
import moment from 'moment'
import { LeftOutline } from 'antd-mobile-icons'

function EditRecommendationModal({
  visible,
  onClose,
  onSave,
  onDelete,
  initialData,
  readOnly = false,
  isAddMode,
  defaultSelectedDateIndex
}) {
  const [category, setCategory] = useState('')
  const [workoutType, setWorkoutType] = useState('')
  const [duration, setDuration] = useState(null)
  const [intensity, setIntensity] = useState('')
  const [description, setDescription] = useState('')
  const [selectedDateIndex, setSelectedDateIndex] = useState(
    moment().isoWeekday() - 1
  )
  const [customPopupVisible, setCustomPopupVisible] = useState(false)
  const [currentField, setCurrentField] = useState('')
  const [customOptions, setCustomOptions] = useState([])
  const [customTempValue, setCustomTempValue] = useState(null)
  const [errors, setErrors] = useState({})

  useEffect(() => {
    if (initialData) {
      setCategory(initialData.category || '')
      setWorkoutType(initialData.workout_type || '')
      const durationInMin = (initialData.duration ?? 0) / 60
      const match = durationOptions.find((opt) => durationInMin <= opt.value)
      setDuration(match ? match.value : null)
      setIntensity(initialData.intensity || '')
      setDescription(initialData.description || '')
      if (initialData.day_of_week >= 0 && initialData.day_of_week <= 6) {
        setSelectedDateIndex(initialData.day_of_week)
      }
    } else {
      setCategory('')
      setWorkoutType('')
      setDuration(null)
      setIntensity('')
      setDescription('')
      setSelectedDateIndex(
        typeof defaultSelectedDateIndex === 'number'
          ? defaultSelectedDateIndex
          : moment().isoWeekday() - 1
      )    
    }
  }, [initialData])

  useEffect(() => {
    if (!visible) return
    setErrors({})

    if (!initialData) {
      setCategory('')
      setWorkoutType('')
      setDuration(null)
      setIntensity('')
      setDescription('')
      setSelectedDateIndex(
        typeof defaultSelectedDateIndex === 'number'
          ? defaultSelectedDateIndex
          : moment().isoWeekday() - 1
      )
    }
  }, [visible])

  const openCustomPopup = (field, options, currentValue) => {
    setCurrentField(field)
    setCustomOptions(options)
    setCustomTempValue(currentValue)
    setCustomPopupVisible(true)
  }

  const handleSave = () => {
    const fieldsToValidate = [
      { key: 'category', value: category },
      { key: 'workoutType', value: workoutType },
      { key: 'duration', value: duration },
      { key: 'intensity', value: intensity }
    ]

    const showErrors = {}

    fieldsToValidate.forEach(({ key, value }) => {
      if (!value) {
        showErrors[key] = 'This field is required!'
      }
    })

    setErrors(showErrors)

    if (Object.keys(showErrors).length > 0) return
    onSave({
      ...initialData,
      category,
      workout_type: workoutType,
      duration: duration * 60,
      intensity,
      description,
      day_of_week: selectedDateIndex,
      isProgramItem: false,
      isPublished: false,
      isSleepActivity: false
    })
  }

  const isPastDay = () => {
    const todayIndex = moment().isoWeekday() - 1
    return selectedDateIndex < todayIndex
  }  

  return (
    <>
      <Popup
        visible={visible}
        onMaskClick={onClose}
        onClose={onClose}
        position="bottom"
        bodyClassName="modal-popup"
      >
        <div className="modal-content">
          <div className="modal-header-wrapper">
            <div className="modal-header">
              <h2>
                {isAddMode
                  ? 'Add Workout'
                  : readOnly
                  ? 'Exercise Details'
                  : 'Edit Workout'}
              </h2>
              <button
                onClick={onClose}
                className="modal-close-btn"
                aria-label="Close"
              >
                Close
              </button>
            </div>
            <WeekDatePicker
              selectedDateIndex={selectedDateIndex}
              onSelectDate={(index) => setSelectedDateIndex(index)}
              readOnly={readOnly}
              isAddMode={isAddMode}
            />
          </div>
          <div className="modal-header-wrapper exercise-container">
            <div style={{ marginBottom: 30 }}>
              <label className="modal-label">Description</label>
              <TextArea
                placeholder="Enter description"
                value={description}
                onChange={(val) => setDescription(val)}
                autoSize={{ minRows: 3, maxRows: 6 }}
                readOnly={readOnly}
                className="description-textarea"
              />
            </div>

            <div style={{ marginBottom: 30 }}>
              <label className="modal-label">Category</label>
              <div
                className={`modal-select-box ${
                  !category ? 'placeholder' : ''
                } ${errors.category ? 'error' : ''}`}
                onClick={() => {
                  if (!readOnly)
                    openCustomPopup('category', categoryOptions, category)
                }}
              >
                {categoryOptions.find((opt) => opt.value === category)?.label ||
                  'Select Category'}
              </div>
              {errors.category && (
                <div className="field-error">{errors.category}</div>
              )}
            </div>
            <div style={{ marginBottom: 30 }}>
              <label className="modal-label">Workout Type</label>
              <div
                className={`modal-select-box ${
                  !workoutType ? 'placeholder' : ''
                } ${errors.workoutType ? 'error' : ''}`}
                onClick={() => {
                  if (!readOnly)
                    openCustomPopup(
                      'workoutType',
                      workoutTypeOptionsMap[category] || [],
                      workoutType
                    )
                }}
              >
                {workoutTypeOptionsMap[category]?.find(
                  (opt) => opt.value === workoutType
                )?.label || workoutType || 'Select Workout Type'}
              </div>
              {errors.workoutType && (
                <div className="field-error">{errors.workoutType}</div>
              )}
            </div>
            <div style={{ marginBottom: 30 }}>
              <label className="modal-label">Workout Duration</label>
              <div
                className={`modal-select-box ${
                  !duration ? 'placeholder' : ''
                } ${errors.duration ? 'error' : ''}`}
                onClick={() => {
                  if (!readOnly)
                    openCustomPopup('duration', durationOptions, duration)
                }}
              >
                {durationOptions.find((opt) => opt.value === duration)?.label ||
                  'Select Workout Duration'}
              </div>
              {errors.duration && (
                <div className="field-error">{errors.duration}</div>
              )}
            </div>
            <div style={{ marginBottom: 30 }}>
              <label className="modal-label">Intensity</label>
              <div
                className={`modal-select-box ${
                  !intensity ? 'placeholder' : ''
                } ${errors.intensity ? 'error' : ''}`}
                onClick={() => {
                  if (!readOnly)
                    openCustomPopup('intensity', intensityOptions, intensity)
                }}
              >
                {intensityOptions.find((opt) => opt.value === intensity)
                  ?.label || 'Select Workout Intensity'}
              </div>
              {errors.intensity && (
                <div className="field-error">{errors.intensity}</div>
              )}
            </div>
          </div>
          {!readOnly && (
            <div className={`modal-button-group${isAddMode ? ' add-mode' : ''}`}>
              {!isAddMode && (
                <Button
                  className="delete-button"
                  onClick={() => onDelete(initialData)}
                >
                  Delete
                </Button>
              )}
              <Button onClick={handleSave} className="save-button" disabled={isPastDay()}>
                Save
              </Button>
            </div>
          )}
        </div>
      </Popup>
      <Popup
        visible={customPopupVisible}
        onMaskClick={() => setCustomPopupVisible(false)}
        position="bottom"
        bodyClassName="custom-popup-body"
      >
        <div className="custom-popup-header">
          <Button
            className="back-arrow"
            onClick={() => setCustomPopupVisible(false)}
            style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}
          >
            <LeftOutline style={{ color: '#000', fontSize: 18, strokeWidth: 2 }} />
          </Button>
          <span className="popup-title">
            {fieldDisplayNameMap[currentField]}
          </span>
        </div>
        <div className="popup-divider" />

        <div className="popup-option-list">
          {customOptions.map((opt) => (
            <div
              key={opt.value}
              className={`popup-option ${
                customTempValue === opt.value ? 'selected' : ''
              }`}
              onClick={() => {
                if (currentField === 'category') {
                  setCategory(opt.value)
                  setErrors((prev) => ({ ...prev, category: '' }))
                  setWorkoutType('')
                } else if (currentField === 'workoutType') {
                  setWorkoutType(opt.value)
                  setErrors((prev) => ({ ...prev, workoutType: '' }))
                } else if (currentField === 'duration') {
                  setDuration(opt.value)
                  setErrors((prev) => ({ ...prev, duration: '' }))
                } else if (currentField === 'intensity') {
                  setIntensity(opt.value)
                  setErrors((prev) => ({ ...prev, intensity: '' }))
                }

                setCustomPopupVisible(false)
              }}
            >
              {opt.label}
            </div>
          ))}
        </div>
      </Popup>
    </>
  )
}

export default EditRecommendationModal
